import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { History, LogIn, LogOut, KeyRound, Send, ShieldAlert, XCircle, Layers } from "lucide-react";
import { getAdminActivity } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const TYPES = {
  login: { label: "Signed in", icon: LogIn, tone: "text-green-400" },
  logout: { label: "Signed out", icon: LogOut, tone: "text-zinc-400" },
  login_failed: { label: "Failed sign-in", icon: XCircle, tone: "text-red-400" },
  login_blocked: { label: "Sign-in blocked (lockout)", icon: ShieldAlert, tone: "text-red-400" },
  password_changed: { label: "Password changed", icon: KeyRound, tone: "text-[#D4AF37]" },
  password_change_failed: { label: "Password change failed", icon: KeyRound, tone: "text-red-400" },
  send: { label: "Treasury send", icon: Send, tone: "text-[#D4AF37]" },
  send_failed: { label: "Treasury send failed", icon: Send, tone: "text-red-400" },
  lp_withdraw: { label: "Pool deposit withdrawn", icon: Layers, tone: "text-[#D4AF37]" },
  lp_withdraw_failed: { label: "Pool withdrawal failed", icon: Layers, tone: "text-red-400" },
};
const FILTERS = [["", "All"], ["login", "Sign-ins"], ["login_failed", "Failed"], ["send", "Sends"], ["lp_withdraw", "Pool"], ["password_changed", "Password"]];
const short = (a) => (a ? `${a.slice(0, 8)}…${a.slice(-6)}` : "");
const when = (iso) => new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" });

const SendDetail = ({ e }) => (
  <span className="text-zinc-300">
    {e.amount} {e.token} → {/^0x[0-9a-fA-F]{40}$/.test(e.to)
      ? <Link to={`/address/${e.to}`} className="table-link font-mono">{short(e.to)}</Link>
      : <span className="font-mono">{e.to}</span>}
    {e.txHash && <> · <Link to={`/tx/${e.txHash}`} data-testid="activity-tx-link" className="table-link font-mono">tx {short(e.txHash)}</Link></>}
    {e.error && <span className="text-red-400"> · {e.error}</span>}
  </span>
);

const Row = ({ e }) => {
  const t = TYPES[e.type] || { label: e.type, icon: History, tone: "text-zinc-400" };
  const Icon = t.icon;
  return (
    <li data-testid="activity-row" data-type={e.type} className="grid grid-cols-[auto_1fr] sm:grid-cols-[auto_1fr_auto] items-center gap-x-3 gap-y-1 p-3 rounded-md bg-[#050505] border border-[#2A2411]">
      <Icon size={15} className={t.tone} />
      <div className="min-w-0 text-sm">
        <p className={`font-medium ${t.tone}`}>{t.label}</p>
        <p className="text-[11px] text-zinc-500 truncate">
          {e.email} · IP <span className="font-mono">{e.ip}</span>
          {e.type.startsWith("send") && <> · <SendDetail e={e} /></>}
        </p>
      </div>
      <span className="col-start-2 sm:col-start-3 text-[11px] font-mono text-zinc-500">{when(e.at)}</span>
    </li>
  );
};

export const ActivityLog = () => {
  const [type, setType] = useState("");
  const { data, isError } = useQuery({ queryKey: ["adminActivity", type], queryFn: () => getAdminActivity(type), refetchInterval: 15000 });
  return (
    <Card data-testid="admin-activity-log" className="border-[#2A2411] bg-[#0A0A0A]">
      <CardHeader className="pb-3 flex flex-row flex-wrap items-center justify-between gap-3 space-y-0">
        <CardTitle className="text-base font-display flex items-center gap-2"><History size={16} className="text-[#D4AF37]" /> Activity Log</CardTitle>
        <div className="flex flex-wrap gap-1">
          {FILTERS.map(([v, l]) => (
            <button key={l} data-testid={`activity-filter-${v || "all"}`} onClick={() => setType(v)}
              className={`text-xs px-3 py-1 rounded-full border transition-colors ${type === v ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-[#2A2411] text-zinc-400 hover:text-white"}`}>{l}</button>
          ))}
        </div>
      </CardHeader>
      <CardContent>
        {isError && <p className="text-sm text-red-400">Could not load activity</p>}
        {data && data.events.length === 0 && <p data-testid="activity-empty" className="text-sm text-zinc-500">No activity recorded yet.</p>}
        {data && data.events.length > 0 && (
          <ul className="space-y-2 max-h-[480px] overflow-y-auto pr-1">{data.events.map((e, i) => <Row key={`${e.at}-${i}`} e={e} />)}</ul>
        )}
        <p className="text-[11px] text-zinc-600 mt-3">Kept for 180 days · refreshes every 15s</p>
      </CardContent>
    </Card>
  );
};
